import { addDoc, setDoc, doc, getDocs, getDoc, query, where, limit, orderBy, startAfter, collection, serverTimestamp, arrayUnion } from "firebase/firestore";
import { db } from "../config/FirebaseConfig"; // db exported from your config

class ProjectApi {
    // '<' operator is used to filter based on less than
    // '<=' operator is used to filter based on less than or equal to
    // '==' operator is used to filter based on equal to
    // '>' operator is used to filter based on greater than
    // '>=' operator is used to filter based on greater than or equal to
    // '!=' operator is used to filter based on not equal to
    // 'array-contains' operator is used to filter based on array contains
    // 'array-contains-any' operator is used to filter based on array contains any
    // 'in' operator is used to filter based on in
    // 'not-in' operator is used to filter based on not in

    /*{
        name: string,
        description: string,
        ownerId: userId,               // Producer or company
        companyId: string | null,
        isActive: boolean,
        isDeleted: boolean,
        createdAt: timestamp,
        updatedAt: timestamp
    }
        
    {
        role: "producer" | "consumer",
        invitedBy: userId,
        joinedAt: timestamp
    }*/

    async getProject(id = null) {
        if (!id) {
            console.error("Invalid project ID:", id);
            return { success: false, error: "Invalid project ID" };
        }
        try {
            const projectDoc = await getDoc(doc(db, "projects", id));
            if (projectDoc.exists()) {
                return { success: true, project: projectDoc.data() };
            } else {
                console.error("Project not found:", id);
                return { success: false, error: "Project not found" };
            }
        } catch (error) {
            console.error("Error fetching project:", error);
            return { success: false, error: error.message };
        }
    }

    async createProject(body = {}) {
        if (!body || !body.name || !body.description || !body.ownerId) {
            console.error("Invalid project data:", body);
            return { success: false, error: "Invalid project data" };
        }
        try {
            const projectRef = await addDoc(collection(db, "projects"), {
                name: body.name,
                description: body.description,
                ownerId: body.ownerId,
                owner: body.owner || null,
                companyId: body.companyId || null,
                company: body.company || null,
                isActive: true,
                isDeleted: false,
                participantIds: [body.ownerId],
                createdAt: serverTimestamp(),
                updatedAt: serverTimestamp(),
            });

            await this.createProjectParticipant(projectRef.id, body.ownerId, "producer", body.ownerId, body.owner, body.companyId, body.company, body.owner);
            return { success: true };
        } catch (error) {
            console.error("Error creating project:", error);
            return { success: false, error: error.message };
        }
    }

    async createProjectParticipant(projectId = null, ownerId = null, role = "consumer", userId = null, owner = null, companyId = null, company = null ,user = null) {
        if (!projectId || !ownerId || !userId) {
            console.error("Invalid project participant data:", { projectId, ownerId, userId });
            return { success: false, error: "Invalid project participant data" };
        }
        try {
            const participantRef = doc(db, "projects", projectId, "participants", userId);
            await setDoc(participantRef, {
                role,
                participant: user || null, // Assuming user is the participant object
                invitedById: ownerId,
                invitedBy: owner, // Assuming ownerId is the user who invited
                companyId: role === "producer" ? companyId || null : null,
                company: role === "producer" ? company || null : null,
                joinedAt: serverTimestamp(),
            }, { merge: true });
            return { success: true };
        } catch (error) {
            console.error("Error creating project participant:", error);
            return { success: false, error: error.message };
        }
    }

    async updateProject(id = null, body = {}) {
        if (!id || !body) {
            console.error("Invalid project data:", { id, body });
            return { success: false, error: "Invalid project data" };
        }
        try {
            const projectRef = doc(db, "projects", id);
            await setDoc(projectRef, {
                ...body,
                updatedAt: serverTimestamp(),
            }, { merge: true });
            return { success: true };
        } catch (error) {
            console.error("Error updating project:", error);
            return { success: false, error: error.message };
        }
    }

    async addProjectParticipantIds(projectId = null, userId) {
        if (!projectId || !userId) {
            console.error("Invalid project or participant ID:", { projectId, userId });
            return { success: false, error: "Invalid project or participant ID" };
        }
        try {
            const projectRef = doc(db, "projects", projectId);
            await setDoc(projectRef, {
                participantIds: arrayUnion(userId),
                updatedAt: serverTimestamp(),
            }, { merge: true });
            return { success: true };
        } catch (error) {
            console.error("Error adding to project participant IDs:", error);
            return { success: false, error: error.message };
        }
        
    }

    async totalUserProjects(userId = null) {
        if (!userId) {
            console.error("Invalid user ID:", userId);
            return { success: false, error: "Invalid user ID" };
        }
        try {
            const q = query(
                collection(db, "projects"),
                where("ownerId", "==", userId),
                where('isDeleted', '==', false)
            );
            const snapshot = await getDocs(q);
            const totalCount = snapshot.size;
            return { success: true, totalCount };
        } catch (error) {
            console.error("Error fetching total user projects:", error);
            return { success: false, error: error.message };
        }
    }

    async getUserProjects(userId = null, lastVisible = null, pageSize = 10, page = 1) {
        if (!userId) {
            console.error("Invalid user ID:", userId);
            return { success: false, error: "Invalid user ID" };
        }
        try {
            let q = query(
                collection(db, "projects"),
                where("ownerId", "==", userId),
                where('isDeleted', '==', false),
                orderBy("createdAt", "desc"),
                limit(pageSize)
            );

            if (page > 1 && lastVisible) {
                q = query(q, startAfter(lastVisible));
            }

            const snapshot = await getDocs(q);
            const projects = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            return { success: true, projects, lastVisible: snapshot.docs[snapshot.docs.length - 1] };
        } catch (error) {
            console.error("Error fetching user projects:", error);
            return { success: false, error: error.message };
        }
    }

    async totalParticipantProjects(userId = null) {
        if (!userId) {
            console.error("Invalid user ID:", userId);
            return { success: false, error: "Invalid user ID" };
        }
        try {
            const q = query(
                collection(db, "projects"),
                where("participantIds", "array-contains", userId),
                where('isDeleted', '==', false)
            );
            const snapshot = await getDocs(q);
            const totalCount = snapshot.size;
            return { success: true, totalCount };
        } catch (error) {
            console.error("Error fetching total participant projects:", error);
            return { success: false, error: error.message };
        }
    }

    async getParticipantProjects(userId = null, lastVisible = null, pageSize = 10, page = 1) {
        if (!userId) {
            console.error("Invalid user ID:", userId);
            return { success: false, error: "Invalid user ID" };
        }
        try {
            //get all projects where user is a participant
            //participants is a sub collection of projects with userid as document id
            let q = query(
                collection(db, "projects"),
                where("participantIds", "array-contains", userId),
                where('isDeleted', '==', false),
                orderBy("createdAt", "desc"),
                limit(pageSize)
            );
            if (page > 1 && lastVisible) {
                q = query(q, startAfter(lastVisible));
            }
            const snapshot = await getDocs(q);
            const projects = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            return { success: true, projects, lastVisible: snapshot.docs[snapshot.docs.length - 1] };
        } catch (error) {
            console.error("Error fetching participant projects:", error);
            return { success: false, error: error.message };
        }
    }

    async getProjectParticipants(projectId = null) {
        if (!projectId) {
            console.error("Invalid project ID:", projectId);
            return { success: false, error: "Invalid project ID" };
        }
        try {
            const q = query(
                collection(db, "projects", projectId, "participants"),
                orderBy("joinedAt", "desc")
            );
            return getDocs(q).then(snapshot => {
                const participants = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                return { success: true, participants };
            });
        } catch (error) {
            console.error("Error fetching project participants:", error);
            return { success: false, error: error.message };
        }
    }
}

export default ProjectApi;